import React, { useState, useEffect } from 'react';
import { Activity, Clock, Cpu, Server, Database, Loader2, ShieldCheck, ShieldAlert, AlertTriangle, Play, Pause, RefreshCw, AlertCircle, Trash2, Shield, ChevronLeft, ChevronRight, User } from 'lucide-react';

const InfrastructureSection = ({
  systemStats,
  systemHealth,
  superAdminApi,
  showStatus,
  isFullPruning, setIsFullPruning,
  isPruningImages, setIsPruningImages,
  isKillingZombies, setIsKillingZombies,
  loadSystemData,
  userResourceUsage
}) => {
  const [isDroppingCaches, setIsDroppingCaches] = useState(false);
  const [isClearingSwap, setIsClearingSwap] = useState(false);
  const [isRebooting, setIsRebooting] = useState(false);
  const [isTogglingMaintenance, setIsTogglingMaintenance] = useState(false);
  const [isKillingRam, setIsKillingRam] = useState(false);
  const [isRestartingProxy, setIsRestartingProxy] = useState(false);

  const [isClearingTemp, setIsClearingTemp] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [isBuilderPruning, setIsBuilderPruning] = useState(false);

  const [rawContainers, setRawContainers] = useState([]);
  const [loadingContainers, setLoadingContainers] = useState(true);
  const [actioningContainer, setActioningContainer] = useState(null);

  const [garbageStats, setGarbageStats] = useState(null);
  const [isGcLoading, setIsGcLoading] = useState(false);
  const [topProcessesModal, setTopProcessesModal] = useState(false);
  const [topProcessesType, setTopProcessesType] = useState('cpu');
  const [topProcessesData, setTopProcessesData] = useState([]);
  const [isFetchingProcesses, setIsFetchingProcesses] = useState(false);
  const [processPage, setProcessPage] = useState(1);

  const [dockerDetailModal, setDockerDetailModal] = useState(false);
  const [dockerDetailType, setDockerDetailType] = useState('images');
  const [dockerDetailData, setDockerDetailData] = useState([]);
  const [isFetchingDockerDetails, setIsFetchingDockerDetails] = useState(false);
  const [detailPage, setDetailPage] = useState(1);

  const fetchDockerDetails = async (type) => {
    setDockerDetailType(type);
    setDockerDetailModal(true);
    setIsFetchingDockerDetails(true);
    setDetailPage(1);
    try {
      let res;
      if (type === 'containers') {
        res = await superAdminApi.getRawContainers();
        setDockerDetailData(res.data?.containers || res.containers || []);
      } else if (type === 'images') {
        res = await superAdminApi.getDockerImages();
        setDockerDetailData(res.data?.images || res.images || []);
      } else if (type === 'volumes') {
        res = await superAdminApi.getDockerVolumes();
        setDockerDetailData(res.data?.volumes || res.volumes || []);
      } else if (type === 'buildCache') {
        res = await superAdminApi.getDockerBuildCache();
        setDockerDetailData(res.cacheItems || []);
      }
    } catch (err) {
      showStatus(`Failed to fetch ${type}: ${err.message}`, 'error');
      setDockerDetailData([]);
    } finally {
      setIsFetchingDockerDetails(false);
    }
  };

  const fetchTopProcesses = async (type) => {
    setTopProcessesType(type);
    setTopProcessesModal(true);
    setIsFetchingProcesses(true);
    setProcessPage(1);
    try {
      const res = await superAdminApi.getTopProcesses(type);
      setTopProcessesData(res.data || []);
    } catch (err) {
      showStatus(`Failed to fetch processes: ${err.message}`, 'error');
      setTopProcessesData([]);
    } finally {
      setIsFetchingProcesses(false);
    }
  };

  const handleKillProcess = async (pid) => {
    if (!window.confirm(`Are you sure you want to force kill process ${pid}?`)) return;
    try {
      const res = await superAdminApi.killProcess(pid);
      showStatus(res.message, 'success');
      fetchTopProcesses(topProcessesType);
    } catch (err) {
      showStatus(err.message, 'error');
    }
  };


  const loadGarbageStats = async () => {
    setIsGcLoading(true);
    try {
      const data = await superAdminApi.getGarbageStats();
      setGarbageStats(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsGcLoading(false);
    }
  };

  const handlePurgeGarbage = async () => {
    if (!window.confirm("Are you sure you want to purge all dead containers, dangling images, and unused volumes? This cannot be undone.")) return;
    try {
      await superAdminApi.runGarbageCollection();
      showStatus('Garbage collection completed successfully', 'success');
      loadGarbageStats();
    } catch (err) {
      showStatus('Failed to run garbage collection', 'error');
    }
  };

  const loadRawContainers = async () => {
    try {
      const res = await superAdminApi.getRawContainers();
      setRawContainers(res.containers || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingContainers(false);
    }
  };

  useEffect(() => {
    loadRawContainers();
    loadGarbageStats();
  }, []);

  const handleContainerAction = async (id, action) => {
    setActioningContainer(id);
    try {
      const res = await superAdminApi.manageRawContainer(id, action);
      showStatus(res.message, 'success');
      await loadRawContainers();
    } catch (e) {
      showStatus(e.message, 'error');
    } finally {
      setActioningContainer(null);
    }
  };

  const load1m = systemHealth?.cpu?.load1m || 0;
  const load5m = systemHealth?.cpu?.load5m || 0;
  const load15m = systemHealth?.cpu?.load15m || 0;
  const uptime = systemHealth?.uptime || 0;
  const cpuPct = systemHealth?.cpu?.usage1mPct || 0;
  const memPct = systemHealth?.memory?.usagePct || 0;
  const diskPct = systemHealth?.disk?.usagePct || 0;

  const dockerSpace = systemHealth?.dockerSpace || null;
  const dockerRunning = systemHealth?.docker?.runningContainers || 0;
  const dockerTotal = systemHealth?.docker?.totalContainers || 0;

  // If docker storage is still calculating on the backend, check back every 3 seconds
  useEffect(() => {
    let interval;
    if (!dockerSpace) {
      interval = setInterval(() => {
        loadSystemData();
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [dockerSpace, loadSystemData]);

  const formatBytesValue = (bytes) => {
    if (bytes === 0 || !bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  };

  const formatUptime = (seconds) => {
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor(seconds % (3600 * 24) / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    return `${d}d ${h}h ${m}m`;
  };

  if (dockerDetailModal) {
    return (
      <div className="sa-section fade-in">
        <div className="dashboard-header" style={{ marginBottom: '1.5rem' }}>
          <div className="dashboard-header-title-area" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={() => setDockerDetailModal(false)} style={{ background: 'var(--surface-hover)', border: '1px solid var(--border)', cursor: 'pointer', color: 'var(--text-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem', borderRadius: '8px', transition: 'all 0.2s' }}>
              <ChevronLeft size={20} />
            </button>
            <div>
              <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Database size={24} color="var(--primary)" />
                Docker Details: {dockerDetailType === 'images' ? 'Images & Storage' : dockerDetailType.charAt(0).toUpperCase() + dockerDetailType.slice(1)}
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.25rem', marginBottom: 0 }}>
                Detailed breakdown of Docker {dockerDetailType === 'images' ? 'images and storage resources' : dockerDetailType}.
              </p>
            </div>
          </div>
          <div className="dashboard-header-actions">
            <button className="sa-btn primary" onClick={() => fetchDockerDetails(dockerDetailType)} disabled={isFetchingDockerDetails}>
              <RefreshCw size={16} className={isFetchingDockerDetails ? "sa-spin" : ""} /> Refresh Data
            </button>
          </div>
        </div>

        {/* Tab Navigation for Docker Storage & Containers Details */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', background: 'var(--surface-hover)', padding: '0.35rem', borderRadius: '12px', width: 'fit-content', border: '1px solid var(--border)' }}>
          <button
            onClick={() => fetchDockerDetails('images')}
            style={{
              padding: '0.45rem 1rem',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              background: dockerDetailType === 'images' ? 'var(--surface)' : 'transparent',
              color: dockerDetailType === 'images' ? 'var(--primary)' : 'var(--text-muted)',
              boxShadow: dockerDetailType === 'images' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            Docker Images
          </button>
          <button
            onClick={() => fetchDockerDetails('containers')}
            style={{
              padding: '0.45rem 1rem',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              background: dockerDetailType === 'containers' ? 'var(--surface)' : 'transparent',
              color: dockerDetailType === 'containers' ? 'var(--primary)' : 'var(--text-muted)',
              boxShadow: dockerDetailType === 'containers' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            Containers
          </button>
          <button
            onClick={() => fetchDockerDetails('volumes')}
            style={{
              padding: '0.45rem 1rem',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              background: dockerDetailType === 'volumes' ? 'var(--surface)' : 'transparent',
              color: dockerDetailType === 'volumes' ? 'var(--primary)' : 'var(--text-muted)',
              boxShadow: dockerDetailType === 'volumes' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            Volumes
          </button>
          <button
            onClick={() => fetchDockerDetails('buildCache')}
            style={{
              padding: '0.45rem 1rem',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              background: dockerDetailType === 'buildCache' ? 'var(--surface)' : 'transparent',
              color: dockerDetailType === 'buildCache' ? 'var(--primary)' : 'var(--text-muted)',
              boxShadow: dockerDetailType === 'buildCache' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            Build Cache
          </button>
        </div>

        <div className="sa-card" style={{ padding: '0', overflow: 'hidden' }}>
          {(() => {
            const detailItemsPerPage = 10;
            const totalDetailPages = Math.ceil(dockerDetailData.length / detailItemsPerPage);
            const paginatedDetailData = dockerDetailData.slice((detailPage - 1) * detailItemsPerPage, detailPage * detailItemsPerPage);

            return (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ background: 'var(--surface-hover)', borderBottom: '1px solid var(--border)' }}>
                        {dockerDetailType === 'containers' ? (
                          <>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Container ID</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Owner</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Name</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Image</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Status</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>CPU</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>RAM</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem', textAlign: 'right' }}>Actions</th>
                          </>
                        ) : dockerDetailType === 'images' ? (
                          <>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Repository</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Tag</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Image ID</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Size</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Created</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem', textAlign: 'right' }}>Actions</th>
                          </>
                        ) : dockerDetailType === 'buildCache' ? (
                          <>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>ID</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Size</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Last Used</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem', textAlign: 'right' }}>Actions</th>
                          </>
                        ) : (
                          <>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Volume Name</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem' }}>Driver</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500, fontSize: '0.85rem', textAlign: 'right' }}>Actions</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {isFetchingDockerDetails ? (
                        <tr><td colSpan={8} style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}><Loader2 className="spinning" size={24} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} /> Loading {dockerDetailType}...</td></tr>
                      ) : paginatedDetailData.length > 0 ? (
                        paginatedDetailData.map((d, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                            {dockerDetailType === 'containers' ? (
                              (() => {
                                const rawName = d.name || '';
                                const nameParts = rawName.split('_');
                                const owner = d.owner || (nameParts.length > 1 ? nameParts[0] : 'System');
                                const displayAppName = nameParts.length > 1 ? nameParts.slice(1).join('_') : rawName;
                                return (
                                  <>
                                    <td style={{ padding: '0.6rem 1rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.82rem' }}>{d.id.substring(0, 12)}</td>
                                    <td style={{ padding: '0.6rem 1rem' }}>
                                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: 'var(--surface-hover, rgba(0,0,0,0.04))', color: 'var(--text-main)', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, border: '1px solid var(--border-subtle, rgba(0,0,0,0.06))' }}>
                                        <User size={12} style={{ opacity: 0.6 }} />
                                        {owner}
                                      </div>
                                    </td>
                                    <td style={{ padding: '0.6rem 1rem', fontWeight: 600 }}>{displayAppName}</td>
                                    <td style={{ padding: '0.6rem 1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>{d.image}</td>
                                    <td style={{ padding: '0.6rem 1rem', color: d.state === 'running' ? 'var(--success)' : 'var(--text-muted)' }}>{d.status}</td>
                                    <td style={{ padding: '0.6rem 1rem', fontWeight: 600 }}>{d.cpu}</td>
                                    <td style={{ padding: '0.6rem 1rem', fontWeight: 600 }}>{d.mem}</td>
                                    <td style={{ padding: '0.6rem 1rem', textAlign: 'right' }}>
                                      <button className="sa-action-btn" style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }} onClick={async () => {
                                        if(window.confirm(`Delete container ${d.name}?`)) {
                                          try {
                                            await superAdminApi.manageRawContainer(d.id, 'delete');
                                            showStatus(`Container deleted`, 'success');
                                            fetchDockerDetails('containers');
                                          } catch(e) {
                                            showStatus(e.message, 'error');
                                          }
                                        }
                                      }} title="Delete Container">
                                        <Trash2 size={16} />
                                      </button>
                                    </td>
                                  </>
                                );
                              })()
                            ) : dockerDetailType === 'images' ? (
                              <>
                                <td style={{ padding: '0.6rem 1rem', fontWeight: 600 }}>{d.repository}</td>
                                <td style={{ padding: '0.6rem 1rem' }}>{d.tag}</td>
                                <td style={{ padding: '0.6rem 1rem', color: 'var(--text-muted)' }}>{d.id}</td>
                                <td style={{ padding: '0.6rem 1rem', fontWeight: 600 }}>{d.size}</td>
                                <td style={{ padding: '0.6rem 1rem', color: 'var(--text-muted)' }}>{d.created}</td>
                                <td style={{ padding: '0.6rem 1rem', textAlign: 'right' }}>
                                  <button className="sa-action-btn" style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }} onClick={async () => {
                                    if(window.confirm(`Delete image ${d.id}?`)) {
                                      try {
                                        await superAdminApi.deleteDockerImage(d.id);
                                        showStatus(`Image deleted`, 'success');
                                        fetchDockerDetails('images');
                                      } catch(e) {
                                        showStatus(e.message, 'error');
                                      }
                                    }
                                  }} title="Delete Image">
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </>
                            ) : dockerDetailType === 'buildCache' ? (
                              <>
                                <td style={{ padding: '0.6rem 1rem', color: 'var(--text-muted)' }}>{d.ID}</td>
                                <td style={{ padding: '0.6rem 1rem', fontWeight: 600 }}>{d.Size}</td>
                                <td style={{ padding: '0.6rem 1rem', color: 'var(--text-muted)' }}>{d.LastUsedAt || 'N/A'}</td>
                                <td style={{ padding: '0.6rem 1rem', textAlign: 'right' }}>
                                  <button className="sa-action-btn" style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }} onClick={async () => {
                                    if(window.confirm(`Delete build cache ${d.ID}?`)) {
                                      try {
                                        await superAdminApi.deleteDockerBuildCache(d.ID);
                                        showStatus(`Build cache deleted`, 'success');
                                        fetchDockerDetails('buildCache');
                                      } catch(e) {
                                        showStatus(e.message, 'error');
                                      }
                                    }
                                  }} title="Delete Build Cache">
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td style={{ padding: '0.6rem 1rem', fontWeight: 600 }}>{d.name}</td>
                                <td style={{ padding: '0.6rem 1rem', color: 'var(--text-muted)' }}>{d.driver}</td>
                                <td style={{ padding: '0.6rem 1rem', textAlign: 'right' }}>
                                  <button className="sa-action-btn" style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }} onClick={async () => {
                                    if(window.confirm(`Delete volume ${d.name}?`)) {
                                      try {
                                        await superAdminApi.deleteDockerVolume(d.name);
                                        showStatus(`Volume deleted`, 'success');
                                        fetchDockerDetails('volumes');
                                      } catch(e) {
                                        showStatus(e.message, 'error');
                                      }
                                    }
                                  }} title="Delete Volume">
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan={8} style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>No {dockerDetailType} found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {totalDetailPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderTop: '1px solid var(--border)', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                      Showing <strong>{((detailPage - 1) * detailItemsPerPage) + 1}</strong> to <strong>{Math.min(detailPage * detailItemsPerPage, dockerDetailData.length)}</strong> of <strong>{dockerDetailData.length}</strong> {dockerDetailType}
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <button className="sa-action-btn" disabled={detailPage === 1} onClick={() => setDetailPage(p => Math.max(1, p - 1))} style={{ padding: '0.4rem 0.75rem' }}>
                        <ChevronLeft size={14} /> Previous
                      </button>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-main)', fontWeight: 600, padding: '0 0.4rem' }}>
                        Page {detailPage} of {totalDetailPages}
                      </span>
                      <button className="sa-action-btn" disabled={detailPage === totalDetailPages} onClick={() => setDetailPage(p => Math.min(totalDetailPages, p + 1))} style={{ padding: '0.4rem 0.75rem' }}>
                        Next <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    );
  }

  if (topProcessesModal) {
    return (
      <div className="sa-section fade-in">
        <div className="dashboard-header" style={{ marginBottom: '2rem' }}>
          <div className="dashboard-header-title-area" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={() => setTopProcessesModal(false)} style={{ background: 'var(--surface-hover)', border: '1px solid var(--border)', cursor: 'pointer', color: 'var(--text-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem', borderRadius: '8px', transition: 'all 0.2s' }}>
              <ChevronLeft size={20} />
            </button>
            <div>
              <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {topProcessesType === 'cpu' && <Cpu size={24} color="var(--primary)" />}
                {topProcessesType === 'ram' && <Database size={24} color="var(--primary)" />}
                {topProcessesType === 'disk' && <Server size={24} color="var(--primary)" />}
                Top Consumers: {topProcessesType.toUpperCase()}
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.25rem', marginBottom: 0 }}>
                Detailed breakdown of processes consuming {topProcessesType.toUpperCase()} resources.
              </p>
            </div>
          </div>
        </div>

        <div className="sa-card" style={{ background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
          {(() => {
            const processItemsPerPage = 10;
            const totalProcessPages = Math.ceil(topProcessesData.length / processItemsPerPage);
            const paginatedProcessesData = topProcessesData.slice((processPage - 1) * processItemsPerPage, processPage * processItemsPerPage);

            return (
              <>
                <div style={{ maxHeight: '75vh', overflowY: 'auto', padding: '0' }}>
                  {isFetchingProcesses ? (
                    <div style={{ padding: '4rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                      <Loader2 size={32} className="sa-spin" style={{ margin: '0 auto 1rem' }} />
                      <br />Gathering real-time process data...
                    </div>
                  ) : (
                    <table className="sa-table" style={{ width: '100%', margin: 0 }}>
                      <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-main)', zIndex: 10 }}>
                        <tr>
                          {topProcessesType === 'disk' ? (
                            <>
                              <th style={{ padding: '0.6rem 1rem' }}>Size</th>
                              <th style={{ padding: '0.6rem 1rem' }}>Path</th>
                            </>
                          ) : (
                            <>
                              <th style={{ padding: '0.6rem 1rem' }}>PID</th>
                              <th style={{ padding: '0.6rem 1rem' }}>User</th>
                              <th style={{ padding: '0.6rem 1rem' }}>%CPU</th>
                              <th style={{ padding: '0.6rem 1rem' }}>%MEM</th>
                              <th style={{ padding: '0.6rem 1rem' }}>Command</th>
                              <th style={{ padding: '0.6rem 1rem', textAlign: 'right' }}>Action</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedProcessesData.map((p, i) => (
                          <tr key={i}>
                            {topProcessesType === 'disk' ? (
                              <>
                                <td style={{ padding: '0.6rem 1rem', fontWeight: 600 }}>{p.size}</td>
                                <td style={{ padding: '0.6rem 1rem', color: 'var(--text-muted)', wordBreak: 'break-all' }}>{p.path}</td>
                              </>
                            ) : (
                              <>
                                <td style={{ padding: '0.6rem 1rem', color: 'var(--text-muted)' }}>{p.pid}</td>
                                <td style={{ padding: '0.6rem 1rem' }}>{p.user}</td>
                                <td style={{ padding: '0.6rem 1rem', color: topProcessesType === 'cpu' && parseFloat(p.cpu) > 10 ? 'var(--warning)' : 'inherit', fontWeight: 600 }}>{p.cpu}%</td>
                                <td style={{ padding: '0.6rem 1rem', color: topProcessesType === 'ram' && parseFloat(p.mem) > 10 ? 'var(--warning)' : 'inherit', fontWeight: 600 }}>{p.mem}%</td>
                                <td style={{ padding: '0.6rem 1rem', color: 'var(--text-muted)', fontSize: '0.85rem', wordBreak: 'break-all' }}>{p.command}</td>
                                <td style={{ padding: '0.6rem 1rem', textAlign: 'right' }}>
                                  <button
                                    onClick={() => handleKillProcess(p.pid)}
                                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s' }}
                                    onMouseOver={(e) => { e.currentTarget.style.background = 'var(--danger)'; e.currentTarget.style.color = '#fff'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--danger)'; }}
                                  >
                                    End
                                  </button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                        {topProcessesData.length === 0 && (
                          <tr><td colSpan={topProcessesType === 'disk' ? 2 : 6} style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>No process data available</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>

                {totalProcessPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderTop: '1px solid var(--border)', flexWrap: 'wrap', gap: '0.5rem', background: 'var(--surface)' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                      Showing <strong>{((processPage - 1) * processItemsPerPage) + 1}</strong> to <strong>{Math.min(processPage * processItemsPerPage, topProcessesData.length)}</strong> of <strong>{topProcessesData.length}</strong> items
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <button className="sa-action-btn" disabled={processPage === 1} onClick={() => setProcessPage(p => Math.max(1, p - 1))} style={{ padding: '0.4rem 0.75rem' }}>
                        <ChevronLeft size={14} /> Previous
                      </button>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-main)', fontWeight: 600, padding: '0 0.4rem' }}>
                        Page {processPage} of {totalProcessPages}
                      </span>
                      <button className="sa-action-btn" disabled={processPage === totalProcessPages} onClick={() => setProcessPage(p => Math.min(totalProcessPages, p + 1))} style={{ padding: '0.4rem 0.75rem' }}>
                        Next <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="sa-section fade-in">
        <div className="dashboard-header">
          <div className="dashboard-header-title-area">
            <h2>Infrastructure & Resource Control</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.25rem' }}>Manage Docker resources, server health, and trigger maintenance protocols globally.</p>
          </div>
        </div>


        {/* System Load & Uptime Stats */}
        <div className="premium-stats-grid" style={{ marginBottom: '2rem', padding: '0 1.5rem', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
          <div className="sa-infra-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>System Load Average</div>
                <span style={{ fontSize: '0.65rem', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', padding: '2px 6px', borderRadius: '4px', fontWeight: 600, letterSpacing: '0.3px' }}>
                  1.0 = 1 CPU Core @ 100%
                </span>
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem', fontStyle: 'italic', opacity: 0.8 }}>
                Represents the average number of processes executing or waiting for CPU time.
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: '4px', letterSpacing: '0.5px' }}>1 Min</span>
                  <span style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-main)', lineHeight: 1 }}>{load1m}</span>
                </div>
                <div style={{ height: '24px', width: '1px', background: 'var(--border-subtle, rgba(255,255,255,0.1))' }}></div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: '4px', letterSpacing: '0.5px' }}>5 Min</span>
                  <span style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-main)', lineHeight: 1 }}>{load5m}</span>
                </div>
                <div style={{ height: '24px', width: '1px', background: 'var(--border-subtle, rgba(255,255,255,0.1))' }}></div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: '4px', letterSpacing: '0.5px' }}>15 Min</span>
                  <span style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-main)', lineHeight: 1 }}>{load15m}</span>
                </div>
              </div>
            </div>
            <div className="sa-infra-icon-wrapper" style={{ background: 'rgba(var(--primary-rgb), 0.1)' }}>
              <Activity size={20} style={{ color: 'var(--primary)' }} />
            </div>
          </div>
          <div className="sa-infra-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem' }}>
            <div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.2rem', fontWeight: 600 }}>Application Uptime</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-main)' }}>{formatUptime(uptime)}</div>
              {systemHealth?.osUptime && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                  Server OS Uptime: {formatUptime(systemHealth.osUptime)}
                </div>
              )}
            </div>
            <div className="sa-infra-icon-wrapper" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
              <Clock size={20} style={{ color: '#22c55e' }} />
            </div>
          </div>
        </div>
        {(() => {
          let totalDeploymentsCpu = 0;
          let totalDeploymentsMem = 0;
          let totalAllocatedMem = 0;
          let totalContainerCount = 0;

          if (userResourceUsage && Array.isArray(userResourceUsage)) {
            userResourceUsage.forEach(u => {
              if (u.deployments && Array.isArray(u.deployments)) {
                u.deployments.forEach(d => {
                  totalDeploymentsCpu += parseFloat(d.cpuPercent || 0);
                  totalDeploymentsMem += parseFloat(d.memUsageBytes || 0);
                  totalAllocatedMem += parseFloat(d.memLimitBytes || 0);
                  totalContainerCount++;
                });
              }
            });
          }

          // System hardware allocations
          const hostCpuCores = systemHealth?.cpu?.count || systemStats?.system?.cpuCount || 1;
          const hostTotalMemBytes = systemHealth?.memory?.totalBytes || (16 * 1024 * 1024 * 1024);
          const hostTotalDiskBytes = systemHealth?.disk?.totalBytes || null;

          // RAM math (Used vs Container Limits Allocated vs Host RAM Capacity)
          const ramUsedBytes = totalDeploymentsMem || (dockerSpace?.containers || 0);
          const containerAllocatedMemBytes = totalAllocatedMem > ramUsedBytes ? totalAllocatedMem : (Math.max(totalContainerCount, 1) * 512 * 1024 * 1024);
          const ramPctOfContainerAlloc = ((ramUsedBytes / containerAllocatedMemBytes) * 100).toFixed(1);
          const ramPctOfHostSystem = ((ramUsedBytes / hostTotalMemBytes) * 100).toFixed(2);

          // Storage math (Used vs Container Volume Quota Allocated vs Host Disk Partition)
          const storageUsedBytes = (dockerSpace?.containers || 0) + (dockerSpace?.images || 0);
          const containerAllocatedStorageBytes = Math.max(storageUsedBytes * 1.5, 10 * 1024 * 1024 * 1024);
          const storagePctOfContainerAlloc = ((storageUsedBytes / containerAllocatedStorageBytes) * 100).toFixed(1);
          const storagePctOfHostSystem = hostTotalDiskBytes ? ((storageUsedBytes / hostTotalDiskBytes) * 100).toFixed(2) : null;

          // CPU math
          const cpuPctOfTotalSystem = ((totalDeploymentsCpu / (hostCpuCores * 100)) * 100).toFixed(2);

          return (
            <>
              {/* Host System Resources Section */}
              <div style={{ margin: '0 1.5rem 1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-main)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Server size={16} color="var(--primary)" /> Host System Resources
                  </h3>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', background: 'var(--surface-hover)', padding: '2px 8px', borderRadius: '10px', fontWeight: 500 }}>
                    Overall Server Hardware
                  </span>
                </div>
                <div className="premium-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
                  {/* Host CPU Card */}
                  <div className="sa-infra-card sa-clickable-card" style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }} onClick={() => fetchTopProcesses('cpu')} onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'} title="Click to view top Host CPU processes">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-main)' }}>
                        <div className="sa-infra-icon-wrapper" style={{ background: 'rgba(var(--primary-rgb), 0.1)' }}>
                          <Cpu size={20} style={{ color: 'var(--primary)' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Host CPU Usage</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Overall system CPU</div>
                        </div>
                      </div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: cpuPct > 80 ? 'var(--danger)' : cpuPct > 50 ? 'var(--warning)' : 'var(--success)' }}>{cpuPct.toFixed(1)}%</div>
                    </div>
                    <div style={{ height: '6px', background: 'var(--surface-hover)', borderRadius: '10px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(cpuPct, 100)}%`, background: cpuPct > 80 ? 'var(--danger)' : cpuPct > 50 ? 'var(--warning)' : 'var(--success)', transition: 'width 0.5s ease' }} />
                    </div>
                    <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{cpuPct > 80 ? 'High load detected on server host.' : 'CPU is operating within normal limits.'}</span>
                    </div>
                  </div>

                  {/* Host Memory Card */}
                  <div className="sa-infra-card sa-clickable-card" style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }} onClick={() => fetchTopProcesses('ram')} onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'} title="Click to view top Host Memory processes">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-main)' }}>
                        <div className="sa-infra-icon-wrapper" style={{ background: 'rgba(var(--primary-rgb), 0.1)' }}>
                          <Database size={20} style={{ color: 'var(--primary)' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Host Memory Usage</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Overall system RAM</div>
                        </div>
                      </div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: memPct > 80 ? 'var(--danger)' : memPct > 50 ? 'var(--warning)' : 'var(--success)' }}>{memPct.toFixed(1)}%</div>
                    </div>
                    <div style={{ height: '6px', background: 'var(--surface-hover)', borderRadius: '10px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(memPct, 100)}%`, background: memPct > 80 ? 'var(--danger)' : memPct > 50 ? 'var(--warning)' : 'var(--success)', transition: 'width 0.5s ease' }} />
                    </div>
                    <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      {memPct > 80 ? 'Host memory usage is critically high.' : 'Memory allocation is stable.'}
                    </div>
                  </div>

                  {/* Host Disk Card */}
                  <div className="sa-infra-card sa-clickable-card" style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }} onClick={() => fetchTopProcesses('disk')} onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'} title="Click to view top Host Disk consumers">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-main)' }}>
                        <div className="sa-infra-icon-wrapper" style={{ background: 'rgba(var(--primary-rgb), 0.1)' }}>
                          <Server size={20} style={{ color: 'var(--primary)' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Host Disk Storage</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Main server disk partition</div>
                        </div>
                      </div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-main)' }}>{diskPct != null ? `${diskPct}%` : 'N/A'}</div>
                    </div>
                    <div style={{ height: '6px', background: 'var(--surface-hover)', borderRadius: '10px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: diskPct != null ? `${Math.min(diskPct, 100)}%` : '0%', background: 'var(--primary)', transition: 'width 0.5s ease' }} />
                    </div>
                    <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      System volume capacity utilized.
                    </div>
                  </div>
                </div>
              </div>

              {/* Deployments Resource Usage Section */}
              <div style={{ margin: '0 1.5rem 2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-main)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Activity size={16} color="#3b82f6" /> Deployments Resource Usage
                  </h3>
                  <span style={{ fontSize: '0.75rem', color: '#3b82f6', background: 'rgba(59, 130, 246, 0.1)', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                    System Deployments & Containers
                  </span>
                </div>
                <div className="premium-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
                  {/* Deployments CPU Card */}
                  <div className="sa-infra-card sa-clickable-card" style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', border: '1px solid rgba(59, 130, 246, 0.25)' }} onClick={() => fetchDockerDetails('containers')} onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'} title="Click to inspect container deployments">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-main)' }}>
                        <div className="sa-infra-icon-wrapper" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                          <Cpu size={20} style={{ color: '#3b82f6' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Deployments CPU</div>
                          <div style={{ fontSize: '0.72rem', color: '#3b82f6', fontWeight: 600 }}>{cpuPctOfTotalSystem}% of {hostCpuCores} Host Cores</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#3b82f6' }}>{totalDeploymentsCpu.toFixed(2)}%</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>Active Load</div>
                      </div>
                    </div>

                    <div style={{ height: '6px', background: 'var(--surface-hover)', borderRadius: '10px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.max(Math.min(totalDeploymentsCpu, 100), totalDeploymentsCpu > 0 ? 3 : 0)}%`, background: '#3b82f6', transition: 'width 0.5s ease' }} />
                    </div>

                    <div style={{ marginTop: '0.85rem', paddingTop: '0.6rem', borderTop: '1px solid var(--border-subtle, rgba(0,0,0,0.05))', fontSize: '0.76rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-main)' }}>
                        <span>Used: <strong>{totalDeploymentsCpu.toFixed(2)}%</strong></span>
                        <span style={{ color: 'var(--text-muted)' }}>Containers: <strong>{totalContainerCount}</strong></span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
                        <span>Host Capacity: <strong>{hostCpuCores} Cores ({hostCpuCores * 100}%)</strong></span>
                        <span style={{ fontWeight: 600, color: '#3b82f6' }}>Details →</span>
                      </div>
                    </div>
                  </div>

                  {/* Deployments Memory Card */}
                  <div className="sa-infra-card sa-clickable-card" style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', border: '1px solid rgba(16, 185, 129, 0.25)' }} onClick={() => fetchDockerDetails('containers')} onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'} title="Click to inspect container deployments">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-main)' }}>
                        <div className="sa-infra-icon-wrapper" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                          <Database size={20} style={{ color: '#10b981' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Deployments RAM</div>
                          <div style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 600 }}>{ramPctOfContainerAlloc}% of Container Quota</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#10b981' }}>{formatBytesValue(ramUsedBytes)}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>Used</div>
                      </div>
                    </div>

                    <div style={{ height: '6px', background: 'var(--surface-hover)', borderRadius: '10px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.max(Math.min(parseFloat(ramPctOfContainerAlloc), 100), ramUsedBytes > 0 ? 3 : 0)}%`, background: '#10b981', transition: 'width 0.5s ease' }} />
                    </div>

                    <div style={{ marginTop: '0.85rem', paddingTop: '0.6rem', borderTop: '1px solid var(--border-subtle, rgba(0,0,0,0.05))', fontSize: '0.76rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-main)' }}>
                        <span>Used: <strong>{formatBytesValue(ramUsedBytes)}</strong></span>
                        <span style={{ color: 'var(--text-muted)' }}>Container Limit: <strong>{formatBytesValue(containerAllocatedMemBytes)}</strong></span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
                        <span>Host System RAM: <strong>{formatBytesValue(hostTotalMemBytes)} ({ramPctOfHostSystem}%)</strong></span>
                        <span style={{ fontWeight: 600, color: '#10b981' }}>Details →</span>
                      </div>
                    </div>
                  </div>

                  {/* Deployments Storage Card */}
                  <div className="sa-infra-card sa-clickable-card" style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', border: '1px solid rgba(168, 85, 247, 0.25)' }} onClick={() => fetchDockerDetails('images')} onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'} title="Click to inspect deployment images & storage resources">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-main)' }}>
                        <div className="sa-infra-icon-wrapper" style={{ background: 'rgba(168, 85, 247, 0.1)' }}>
                          <Server size={20} style={{ color: '#a855f7' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Deployments Storage</div>
                          <div style={{ fontSize: '0.72rem', color: '#a855f7', fontWeight: 600 }}>{storagePctOfContainerAlloc}% of Container Quota</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#a855f7' }}>{formatBytesValue(storageUsedBytes)}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>Used</div>
                      </div>
                    </div>

                    <div style={{ height: '6px', background: 'var(--surface-hover)', borderRadius: '10px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.max(Math.min(parseFloat(storagePctOfContainerAlloc), 100), storageUsedBytes > 0 ? 3 : 0)}%`, background: '#a855f7', transition: 'width 0.5s ease' }} />
                    </div>

                    <div style={{ marginTop: '0.85rem', paddingTop: '0.6rem', borderTop: '1px solid var(--border-subtle, rgba(0,0,0,0.05))', fontSize: '0.76rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-main)' }}>
                        <span>Used: <strong>{formatBytesValue(storageUsedBytes)}</strong></span>
                        <span style={{ color: 'var(--text-muted)' }}>Container Quota: <strong>{formatBytesValue(containerAllocatedStorageBytes)}</strong></span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
                        <span>Host Storage: <strong>{hostTotalDiskBytes ? `${formatBytesValue(hostTotalDiskBytes)} (${storagePctOfHostSystem}%)` : 'Server Partition'}</strong></span>
                        <span style={{ fontWeight: 600, color: '#a855f7' }}>Details →</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {/* Docker Storage Details */}
        <div style={{ margin: '0 1.5rem 2rem' }}>
          <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-main)' }}>
            <Database size={18} color="var(--primary)" /> Docker Storage Details
          </h3>
          {dockerSpace ? (
            <div className="premium-stats-grid" style={{ gridTemplateColumns: 'repeat(6, 1fr)', gap: '1.5rem' }}>
              <div className="sa-infra-card sa-clickable-card" style={{ padding: '1.25rem', cursor: 'pointer' }} onClick={() => fetchDockerDetails('containers')}>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 600 }}>Active Containers</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)' }}>
                  {dockerRunning} <span style={{ fontSize: '1rem', color: 'var(--text-muted)' }}>/ {dockerTotal}</span>
                </div>
              </div>
              <div className="sa-infra-card" style={{ padding: '1.5rem' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 600 }}>Total Usage</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)' }}>{formatBytesValue(dockerSpace.total)}</div>
              </div>
              <div className="sa-infra-card sa-clickable-card" style={{ padding: '1.5rem', cursor: 'pointer' }} onClick={() => fetchDockerDetails('images')}>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 600 }}>Images</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)' }}>{formatBytesValue(dockerSpace.images)}</div>
              </div>
              <div className="sa-infra-card sa-clickable-card" style={{ padding: '1.5rem', cursor: 'pointer' }} onClick={() => fetchDockerDetails('containers')}>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 600 }}>Containers</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)' }}>{formatBytesValue(dockerSpace.containers)}</div>
              </div>
              <div className="sa-infra-card sa-clickable-card" style={{ padding: '1.5rem', cursor: 'pointer' }} onClick={() => fetchDockerDetails('volumes')}>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 600 }}>Volumes</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)' }}>{formatBytesValue(dockerSpace.volumes)}</div>
              </div>
              <div className="sa-infra-card sa-clickable-card" style={{ padding: '1.5rem', cursor: 'pointer' }} onClick={() => fetchDockerDetails('buildCache')}>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 600 }}>Build Cache</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)' }}>{formatBytesValue(dockerSpace.buildCache)}</div>
              </div>
            </div>
          ) : (
            <div style={{ color: 'var(--text-muted)', padding: '1.25rem', background: 'var(--surface-hover)', borderRadius: '12px', border: '1px solid var(--border)' }}>
              Loading Docker storage data...
            </div>
          )}
        </div>

        {/* Garbage Collection Section */}
        <div style={{ margin: '0 1.5rem 2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-main)', margin: 0 }}>
              <Trash2 size={18} color="var(--danger)" /> Garbage Collection
            </h3>
            <button className="sa-action-btn" onClick={loadGarbageStats} disabled={isGcLoading} title="Refresh Garbage Stats">
              <RefreshCw size={16} className={isGcLoading ? "sa-spin" : ""} />
            </button>
          </div>

          <div className="sa-card" style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center', padding: '2rem' }}>
            {isGcLoading ? (
              <div className="sa-loading"><Loader2 className="spinning" size={32} style={{ margin: '0 auto' }} /> Scanning system...</div>
            ) : garbageStats ? (
              <div>
                <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>Safely purge dead containers, volumes, and dangling images from suspended accounts.</p>
                <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: '2.5rem' }}>
                  <div style={{ padding: '1.5rem', background: 'var(--surface-hover)', borderRadius: '12px', flex: '1', margin: '0 0.5rem', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '0.5rem' }}>{garbageStats.stoppedContainers}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Dead Containers</div>
                  </div>
                  <div style={{ padding: '1.5rem', background: 'var(--surface-hover)', borderRadius: '12px', flex: '1', margin: '0 0.5rem', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '0.5rem' }}>{garbageStats.unusedVolumes}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Orphaned Volumes</div>
                  </div>
                  <div style={{ padding: '1.5rem', background: 'var(--surface-hover)', borderRadius: '12px', flex: '1', margin: '0 0.5rem', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '0.5rem' }}>{garbageStats.danglingImages}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Dangling Images</div>
                  </div>
                </div>

                {(garbageStats.stoppedContainers > 0 || garbageStats.unusedVolumes > 0 || garbageStats.danglingImages > 0) ? (
                  <button
                    onClick={handlePurgeGarbage}
                    style={{
                      background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '1rem 2rem',
                      borderRadius: '8px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      boxShadow: '0 10px 20px -5px rgba(239, 68, 68, 0.4)'
                    }}
                  >
                    <Trash2 size={20} />
                    Purge Dead Data
                  </button>
                ) : (
                  <div style={{ color: '#22c55e', fontWeight: '500', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <Shield size={20} /> System is clean. No dead data found.
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)' }}>Failed to load garbage stats.</div>
            )}
          </div>
        </div>


        {/* Advanced Resource Controls (Added via AI) */}
        <div style={{ padding: '0 1.5rem' }}>
          <h3 className="sa-section-title" style={{ marginTop: '2.5rem', marginBottom: '1.5rem' }}>
            <Server size={20} color="var(--primary)" /> Advanced Host Controls
          </h3>
          <div className="premium-stats-grid" style={{ marginBottom: '2rem', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
            {/* Drop Caches */}
            <div className="sa-infra-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
              <h4 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0' }}>Drop System Caches</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Frees up inactive memory (PageCache, inodes).</p>
              <button className="action-btn-premium" disabled={isDroppingCaches} onClick={async () => {
                if (!window.confirm('Drop caches?')) return;
                setIsDroppingCaches(true);
                try {
                  const r = await superAdminApi.dropCaches();
                  showStatus(r.message, 'success');
                } catch (e) { showStatus(e.message, 'error'); } finally { setIsDroppingCaches(false); }
              }}>
                {isDroppingCaches ? <Loader2 size={16} className="sa-spin" /> : 'Drop Caches'}
              </button>
            </div>

            {/* Clear Swap */}
            <div className="sa-infra-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
              <h4 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0' }}>Clear Swap Memory</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Flushes all swap memory. May cause OOM if RAM is full.</p>
              <button className="action-btn-premium danger" disabled={isClearingSwap} onClick={async () => {
                if (!window.confirm('Clear swap?')) return;
                setIsClearingSwap(true);
                try {
                  const r = await superAdminApi.clearSwap();
                  showStatus(r.message, 'success');
                } catch (e) { showStatus(e.message, 'error'); } finally { setIsClearingSwap(false); }
              }}>
                {isClearingSwap ? <Loader2 size={16} className="sa-spin" /> : 'Clear Swap'}
              </button>
            </div>

            {/* Reboot Host */}
            <div className="sa-infra-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
              <h4 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0', color: 'var(--danger)' }}>Reboot Host Machine</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Hard reboot of the underlying server.</p>
              <button className="action-btn-premium danger" disabled={isRebooting} onClick={async () => {
                if (window.prompt('Type REBOOT to confirm') !== 'REBOOT') return;
                setIsRebooting(true);
                try {
                  const r = await superAdminApi.rebootHost();
                  showStatus(r.message, 'success');
                } catch (e) { showStatus(e.message, 'error'); } finally { setIsRebooting(false); }
              }}>
                {isRebooting ? <Loader2 size={16} className="sa-spin" /> : 'Reboot Host'}
              </button>
            </div>
          </div>

          <h3 className="sa-section-title" style={{ marginTop: '2.5rem', marginBottom: '1.5rem' }}>
            <ShieldAlert size={20} color="var(--primary)" /> Emergency Procedures
          </h3>
          <div className="premium-stats-grid" style={{ marginBottom: '2rem', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
            {/* Maintenance Mode */}
            <div className="sa-infra-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
              <h4 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0', color: 'var(--warning)' }}>Global Suspend</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Pauses all user containers.</p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="action-btn-premium" style={{ flex: 1 }} disabled={isTogglingMaintenance} onClick={async () => {
                  if (!window.confirm('Suspend all containers?')) return;
                  setIsTogglingMaintenance(true);
                  try {
                    const r = await superAdminApi.toggleMaintenanceMode('pause');
                    showStatus(r.message, 'success');
                  } catch (e) { showStatus(e.message, 'error'); } finally { setIsTogglingMaintenance(false); }
                }}>
                  {isTogglingMaintenance ? <Loader2 size={16} className="sa-spin" /> : <><Pause size={14} style={{ marginRight: '0.25rem' }} /> Suspend</>}
                </button>
                <button className="action-btn-premium" style={{ flex: 1 }} disabled={isTogglingMaintenance} onClick={async () => {
                  if (!window.confirm('Resume all containers?')) return;
                  setIsTogglingMaintenance(true);
                  try {
                    const r = await superAdminApi.toggleMaintenanceMode('resume');
                    showStatus(r.message, 'success');
                  } catch (e) { showStatus(e.message, 'error'); } finally { setIsTogglingMaintenance(false); }
                }}>
                  <Play size={14} style={{ marginRight: '0.25rem' }} /> Resume
                </button>
              </div>
            </div>

            {/* Kill RAM */}
            <div className="sa-infra-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
              <h4 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0' }}>Kill Highest RAM Consumer</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Instantly force-kill the largest container.</p>
              <button className="action-btn-premium danger" disabled={isKillingRam} onClick={async () => {
                if (!window.confirm('Kill highest RAM container?')) return;
                setIsKillingRam(true);
                try {
                  const r = await superAdminApi.killHighestRam();
                  showStatus(r.message, 'success');
                } catch (e) { showStatus(e.message, 'error'); } finally { setIsKillingRam(false); }
              }}>
                {isKillingRam ? <Loader2 size={16} className="sa-spin" /> : 'Execute Kill'}
              </button>
            </div>

            {/* Restart Proxy */}
            <div className="sa-infra-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
              <h4 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0' }}>Restart Routing Layer</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Restart Traefik/Nginx proxy.</p>
              <button className="action-btn-premium primary" disabled={isRestartingProxy} onClick={async () => {
                setIsRestartingProxy(true);
                try {
                  const r = await superAdminApi.restartProxy();
                  showStatus(r.message, 'success');
                } catch (e) { showStatus(e.message, 'error'); } finally { setIsRestartingProxy(false); }
              }}>
                {isRestartingProxy ? <Loader2 size={16} className="sa-spin" /> : <><RefreshCw size={14} style={{ marginRight: '0.25rem' }} /> Restart Proxy</>}
              </button>
            </div>
          </div>
        </div>
        {/* Emergency Brakes */}
        <div style={{ padding: '0 1.5rem 1.5rem' }}>
          <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-main)' }}>
            <AlertCircle size={18} color="var(--danger)" /> Emergency Maintenance Protocols
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {/* Action 4: Clear Temp Files */}
            <div className="sa-infra-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0', color: 'var(--text-main)' }}>Clear Temp Files</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                  Removes cached files, dangling builds, and temporary deployment artifacts to free up rapid storage.
                </p>
              </div>
              <button
                className="action-btn-premium"
                style={{ marginTop: '1.5rem', width: '100%', justifyContent: 'center' }}
                disabled={isClearingTemp}
                onClick={async () => {
                  if (!window.confirm('Clear all temporary files?')) return;
                  setIsClearingTemp(true);
                  showStatus('Clearing temp files...', 'info');
                  try {
                    const r = await superAdminApi.clearTempFiles();
                    showStatus(r.message || 'Done', 'success');
                  } catch (e) {
                    showStatus(e.message, 'error');
                  } finally {
                    setIsClearingTemp(false);
                  }
                }}
              >
                {isClearingTemp ? <><Loader2 size={16} className="sa-spin" style={{ marginRight: '0.5rem' }} /> Clearing...</> : 'Execute Clear Temp'}
              </button>
            </div>

            {/* Action 5: Sync Routing */}
            <div className="sa-infra-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0', color: 'var(--text-main)' }}>Sync & Reconcile Routing</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                  Manually triggers Nginx gateway routing reconciliation. Useful if some deployments are stuck in 502 Bad Gateway.
                </p>
              </div>
              <button
                className="action-btn-premium"
                style={{ marginTop: '1.5rem', width: '100%', justifyContent: 'center' }}
                disabled={isReconciling}
                onClick={async () => {
                  if (!window.confirm('Reconcile Nginx routing?')) return;
                  setIsReconciling(true);
                  showStatus('Reconciling routing...', 'info');
                  try {
                    await superAdminApi.reconcileRouting();
                    showStatus('Routing synchronized', 'success');
                  } catch (e) {
                    showStatus(e.message, 'error');
                  } finally {
                    setIsReconciling(false);
                  }
                }}
              >
                {isReconciling ? <><Loader2 size={16} className="sa-spin" style={{ marginRight: '0.5rem' }} /> Reconciling...</> : 'Execute Routing Sync'}
              </button>
            </div>

            {/* Action 6: Prune Builder Cache */}
            <div className="sa-infra-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0', color: 'var(--text-main)' }}>Prune Build Cache</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                  Removes unused Docker build caches generated by Docker Buildx. Safely recovers massive amounts of storage.
                </p>
              </div>
              <button
                className="action-btn-premium"
                style={{ marginTop: '1.5rem', width: '100%', justifyContent: 'center' }}
                disabled={isBuilderPruning}
                onClick={async () => {
                  if (!window.confirm('Prune Docker builder cache?')) return;
                  setIsBuilderPruning(true);
                  showStatus('Pruning builder cache...', 'info');
                  try {
                    await superAdminApi.dockerBuilderPrune();
                    showStatus('Cache pruned successfully', 'success');
                  } catch (e) {
                    showStatus(e.message, 'error');
                  } finally {
                    setIsBuilderPruning(false);
                  }
                }}
              >
                {isBuilderPruning ? <><Loader2 size={16} className="sa-spin" style={{ marginRight: '0.5rem' }} /> Pruning...</> : 'Execute Cache Prune'}
              </button>
            </div>


            {/* Action 1 */}
            <div className="sa-infra-card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0', color: 'var(--text-main)' }}>Prune Unused Images</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                  Removes all Docker images that are not currently referenced by any active container. This is a safe operation that recovers disk space.
                </p>
              </div>
              <button
                className="action-btn-premium danger"
                style={{ marginTop: '1.5rem', width: '100%', justifyContent: 'center' }}
                disabled={isPruningImages}
                onClick={async () => {
                  if (!window.confirm('Prune all unused Docker images?')) return;
                  setIsPruningImages(true);
                  showStatus('Pruning images...', 'info');
                  try {
                    const r = await superAdminApi.dockerPruneImages();
                    showStatus(r.output || 'Done', 'success');
                  } catch (e) {
                    showStatus(e.message, 'error');
                  } finally {
                    setIsPruningImages(false);
                  }
                }}
              >
                {isPruningImages ? <><Loader2 size={16} className="sa-spin" style={{ marginRight: '0.5rem' }} /> Pruning...</> : 'Execute Image Prune'}
              </button>
            </div>

            {/* Action 2 */}
            <div className="sa-infra-card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0', color: 'var(--text-main)' }}>Kill Zombie Containers</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                  Forcefully stops and removes orphaned or dead containers that are consuming system resources without serving traffic.
                </p>
              </div>
              <button
                className="action-btn-premium danger"
                style={{ marginTop: '1.5rem', width: '100%', justifyContent: 'center' }}
                disabled={isKillingZombies}
                onClick={async () => {
                  if (!window.confirm('Kill all zombie containers?')) return;
                  setIsKillingZombies(true);
                  showStatus('Killing zombies...', 'info');
                  try {
                    const r = await superAdminApi.dockerKillZombies();
                    showStatus(`Killed ${r.killed} containers`, 'success');
                  } catch (e) {
                    showStatus(e.message, 'error');
                  } finally {
                    setIsKillingZombies(false);
                  }
                }}
              >
                {isKillingZombies ? <><Loader2 size={16} className="sa-spin" style={{ marginRight: '0.5rem' }} /> Terminating...</> : 'Execute Zombie Kill'}
              </button>
            </div>

            {/* Action 3 */}
            <div className="sa-infra-danger-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0', color: 'var(--danger)' }}>Full System Prune</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                  <strong>WARNING:</strong> This will remove all unused containers, networks, images, and optionally volumes. This is a destructive operation.
                </p>
              </div>
              <button
                className="action-btn-premium danger"
                style={{ marginTop: '1.5rem', width: '100%', justifyContent: 'center', background: 'var(--danger)', color: '#fff', borderColor: 'var(--danger)' }}
                disabled={isFullPruning}
                onClick={async () => {
                  if (!window.confirm('Full Docker system prune? This removes volumes too.')) return;
                  setIsFullPruning(true);
                  showStatus('Running full prune...', 'info');
                  try {
                    await superAdminApi.dockerPrune();
                    showStatus('Full prune complete', 'success');
                  } catch (e) {
                    showStatus(e.message, 'error');
                  } finally {
                    setIsFullPruning(false);
                  }
                }}
              >
                {isFullPruning ? <><Loader2 size={16} className="sa-spin" style={{ marginRight: '0.5rem' }} /> Pruning System...</> : 'Execute Full Prune'}
              </button>
            </div>

          </div>
        </div>
      </div>


    </>
  );
};

export default InfrastructureSection;
